// Helper function to convert Excel date number to DD-MM-YYYY string
export const excelDateToDDMMYYYY = (excelDate) => {
    if (typeof excelDate === 'number' && excelDate > 0) {
        const date = new Date(Math.round((excelDate - 25569) * 86400 * 1000));
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    }
    return excelDate;
};

// Function to format date string to DD-MM-YYYY
export const formatDateToDDMMYYYY = (dateStr) => {
    if (!dateStr || (typeof dateStr === 'string' && dateStr.trim() === '')) {
        return '-';
    }
    
    // Jika sudah format DD-MM-YYYY
    if (typeof dateStr === 'string') {
        // Cek pattern DD-MM-YYYY
        const ddMmYyyyPattern = /^\d{2}-\d{2}-\d{4}$/;
        if (ddMmYyyyPattern.test(dateStr.trim())) {
            return dateStr.trim();
        }
        
        // Cek pattern YYYY-MM-DD
        const yyyyMmDdPattern = /^\d{4}-\d{2}-\d{2}$/;
        if (yyyyMmDdPattern.test(dateStr.trim())) {
            const [year, month, day] = dateStr.trim().split('-');
            return `${day}-${month}-${year}`;
        }
        
        // Coba parse sebagai date object
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        }
    }
    
    // Jika number (Excel date serial)
    if (typeof dateStr === 'number') {
        return excelDateToDDMMYYYY(dateStr);
    }
    
    return String(dateStr).trim();
};

// Fungsi untuk memformat angka dengan pemisah ribuan TANPA desimal
export const formatNumber = (value) => {
    if (value === undefined || value === null || value === '' || value === '-') return '-';
    
    // Jika value sudah string "-", kembalikan "-"
    if (value === '-') return '-';
    
    try {
        const num = typeof value === 'string' 
            ? parseFloat(value.replace(/,/g, '')) 
            : value;
        
        if (!isNaN(num)) {
            // Gunakan Math.round() untuk menampilkan tanpa desimal
            return Math.round(num).toLocaleString('id-ID');
        }
        
        return value;
    } catch {
        return value;
    }
};

// Fungsi untuk memformat angka desimal dengan 2 digit di belakang koma
export const formatDecimalWithTwoDigits = (value) => {
  if (value === undefined || value === null || value === '' || value === '-') return '-';
  
  // Jika value sudah string "-", kembalikan "-"
  if (value === '-') return '-';
  
  try {
    if (typeof value === 'string') {
      let formattedValue = value.replace(',', '.');
      const num = parseFloat(formattedValue);
      if (!isNaN(num)) {
        // Format dengan 2 digit di belakang koma
        return num.toFixed(2);
      }
      return value;
    }
    
    if (typeof value === 'number') {
      // Format dengan 2 digit di belakang koma
      return value.toFixed(2);
    }
    
    return '-';
  } catch {
    return value;
  }
};

// Fungsi untuk memformat angka desimal TANPA desimal di belakang koma
export const formatDecimal = (value) => {
    if (value === undefined || value === null || value === '' || value === '-') return '-';
    
    // Jika value sudah string "-", kembalikan "-"
    if (value === '-') return '-';
    
    try {
        if (typeof value === 'string') {
            let formattedValue = value.replace(',', '.');
            const num = parseFloat(formattedValue);
            if (!isNaN(num)) {
                // Gunakan Math.round() untuk menghilangkan desimal
                return Math.round(num).toLocaleString('id-ID');
            }
            return value;
        }
        
        if (typeof value === 'number') {
            // Gunakan Math.round() untuk menghilangkan desimal
            return Math.round(value).toLocaleString('id-ID');
        }
        
        return '-';
    } catch {
        return value;
    }
};

// Fungsi untuk memformat tanggal dari format DD-MM-YYYY (jika ada)
export const formatDateDisplay = (dateStr) => {
    if (!dateStr || dateStr === '-') return '-';
    
    // Jika sudah format DD-MM-YYYY, tampilkan apa adanya
    const ddMmYyyyPattern = /^\d{2}-\d{2}-\d{4}$/;
    if (ddMmYyyyPattern.test(dateStr.trim())) {
        return dateStr.trim();
    }
    
    // Jika format lain, coba konversi ke DD-MM-YYYY
    return formatDateToDDMMYYYY(dateStr);
};

// Fungsi untuk menampilkan teks biasa (untuk kolom non-angka)
export const formatText = (value) => {
    if (value === undefined || value === null || value === '' || value === '-') {
        return '-';
    }
    return String(value);
};

// Format nilai untuk Excel import (tanpa pemisah ribuan)
export const formatValueAsIs = (value) => {
    if (value === undefined || value === null) {
        return '-';
    }
    
    if (typeof value === 'string' && value.trim() === '') {
        return '-';
    }
    
    if (value === 0 || value === '0') {
        return '-';
    }
    
    // Try to parse as number
    let num;
    if (typeof value === 'number') {
        num = value;
    } else if (typeof value === 'string') {
        // Hapus semua pemisah ribuan dan ganti koma dengan titik
        const cleanValue = value.toString().trim().replace(/\./g, '').replace(',', '.');
        num = parseFloat(cleanValue);
    }
    
    if (!isNaN(num) && num !== 0) {
        // Untuk mempertahankan 4 digit desimal dengan trailing zeros
        const factor = Math.pow(10, 4);
        const rounded = Math.floor(num * factor) / factor;
        
        // Format dengan 4 digit desimal, termasuk trailing zeros
        return rounded.toFixed(4);
    }
    
    return String(value).trim();
};

// Fungsi untuk memformat angka dengan 2 digit desimal menggunakan Math.round()
export const formatToTwoDecimalWithRound = (value) => {
    if (value === undefined || value === null || value === '') {
        return '0.00';
    }
    
    // Konversi ke number
    const num = typeof value === 'string' ? parseFloat(value) : Number(value);
    
    // Jika bukan angka yang valid, return 0.00
    if (isNaN(num)) {
        return '0.00';
    }
    
    // Gunakan Math.round() untuk membulatkan ke 2 digit desimal
    const rounded = Math.round(num * 100) / 100;
    
    // Format ke 2 digit desimal
    return rounded.toFixed(2);
};